import {
    devicesConstants,
    mobileDevicesConstants,
    licensesConstants,
    VMConstants,
    usersWithoutComputersConstants
} from '../actions';
import { PersistentPaginationModel } from './pagination';
import { mapFiltersToSearchString } from './mappers';
import { history } from '../history';

/*
 * Function that creates reducer for filters with common handlers like UPDATE_FILTERS, RESET_FILTERS, ...
 * Also there is a possibility to extend this reducer by providing "additionalActionHandlers"
 * "additionalActionHandlers" is an object, where each property is a function that gets state & action and returns new state
 */

export const createFiltersReducer = (actionsPrefix, initialState, additionalActionHandlers = {}) => {
    const paginationModel = new PersistentPaginationModel(actionsPrefix);

    const initialItemsPerPage = paginationModel.getItemsPerPage();

    initialState.appliedFilters.itemsPerPage = initialItemsPerPage;
    initialState.currentFilters.itemsPerPage = initialItemsPerPage;

    return (state = initialState, action) => {
        if (additionalActionHandlers[action.type]) {
            return additionalActionHandlers[action.type](state, action);
        } else {
            switch (action.type) {
                case `${actionsPrefix}_${UPDATE_FILTERS}`:
                    return {
                        ...state,
                        currentFilters: {
                            ...state.currentFilters,
                            ...action.payload,
                            itemsPerPage: paginationModel.getItemsPerPage(),
                            isAppliedFilter: false
                        }
                    };
                case `${actionsPrefix}_${APPLY_FILTERS}`:
                    if (action.payload.itemsPerPage) {
                        paginationModel.setItemsPerPage(action.payload.itemsPerPage);
                    }

                    return {
                        ...state,
                        appliedFilters: {
                            ...state.appliedFilters,
                            ...action.payload,
                            itemsPerPage: paginationModel.getItemsPerPage(),
                            isAppliedFilter: true
                        }
                    };
                case `${actionsPrefix}_${REBUILD_APPLY_FILTERS}`:
                case `${actionsPrefix}_${REBUILD_APPLY_FILTERS_SILENTLY}`:
                    return {
                        appliedFilters: {
                            ...initialState.appliedFilters,
                            ...action.payload,
                            itemsPerPage: paginationModel.getItemsPerPage(),
                            isAppliedFilter: true
                        },
                        currentFilters: {
                            ...initialState.currentFilters,
                            ...action.payload,
                            itemsPerPage: paginationModel.getItemsPerPage(),
                            isAppliedFilter: true
                        }
                    };
                case `${actionsPrefix}_${RESET_APPLY_FILTERS}`:
                case `${actionsPrefix}_${RESET_APPLY_FILTERS_SILENTLY}`:
                    return {
                        appliedFilters: {
                            ...initialState.appliedFilters,
                            itemsPerPage: paginationModel.getItemsPerPage(),
                            isAppliedFilter: false
                        },
                        currentFilters: {
                            ...initialState.currentFilters,
                            itemsPerPage: paginationModel.getItemsPerPage(),
                            isAppliedFilter: false
                        }
                    };
                case devicesConstants.DEVICES_CLEARED:
                case mobileDevicesConstants.MOBILE_DEVICES_CLEARED:
                case licensesConstants.LICENSES_CLEARED:
                case licensesConstants.LICENSE_REVIEW_CLEARED:
                case VMConstants.VMs_CLEARED:
                case usersWithoutComputersConstants.UWC_CLEARED:
                    return {
                        appliedFilters: {
                            ...initialState.appliedFilters,
                            itemsPerPage: paginationModel.getItemsPerPage(),
                            isAppliedFilter: false
                        },
                        currentFilters: {
                            ...initialState.currentFilters,
                            itemsPerPage: paginationModel.getItemsPerPage(),
                            isAppliedFilter: false
                        }
                    };
                default:
                    return state;
            }
        }
    };
};

export const UPDATE_FILTERS = 'UPDATE_FILTERS';
export const APPLY_FILTERS = 'APPLY_FILTERS';
export const REBUILD_APPLY_FILTERS = 'REBUILD_APPLY_FILTERS';
export const REBUILD_APPLY_FILTERS_SILENTLY = 'REBUILD_APPLY_FILTERS_SILENTLY';
export const RESET_APPLY_FILTERS = 'RESET_APPLY_FILTERS';
export const RESET_APPLY_FILTERS_SILENTLY = 'RESET_APPLY_FILTERS_SILENTLY';

/*
 * Function that creates common actions connected to filters.
 */

const buildFiltersUrl = (filters = {}) => {
    const searchString = mapFiltersToSearchString(filters);

    return searchString === ''
        ? window.location.pathname
        : `${history.location.pathname}?${mapFiltersToSearchString(filters)}`;
};

export const createFiltersActions = actionsPrefix => ({
    updateFilters: filters => ({
        type: `${actionsPrefix}_${UPDATE_FILTERS}`,
        payload: filters
    }),
    resetApplyFilters: () => {
        history.push(buildFiltersUrl());

        return {
            type: `${actionsPrefix}_${RESET_APPLY_FILTERS}`
        };
    },
    resetApplyFiltersSilently: () => ({
        type: `${actionsPrefix}_${RESET_APPLY_FILTERS_SILENTLY}`
    }),
    applyFilters: filters => {
        const url = buildFiltersUrl(filters);
        if (typeof url === 'string') {
            history.push(url);
        } else {
            console.error('buildFiltersUrl returned non-string:', url);
        }

        return {
            type: `${actionsPrefix}_${APPLY_FILTERS}`,
            payload: filters
        };
    },
    rebuildApplyFilters: filters => {
        const url = buildFiltersUrl(filters);
        if (typeof url === 'string') {
            history.push(url);
        } else {
            console.error('buildFiltersUrl returned non-string:', url);
        }

        return {
            type: `${actionsPrefix}_${REBUILD_APPLY_FILTERS}`,
            payload: filters
        };
    },
    rebuildApplyFiltersSilently: filters => ({
        type: `${actionsPrefix}_${REBUILD_APPLY_FILTERS_SILENTLY}`,
        payload: filters
    })
});
